import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { motion } from 'framer-motion';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import useScrollToTop from '../hooks/useScrollToTop';

const USGreenCard: React.FC = () => {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  useScrollToTop();

  const programs = [
    {
      title: t('nav.eb1aNiw'),
      description: t('us.eb1a.description'),
      backgroundColor: '#9c27b0',
      path: '/us-green-card/eb1a-niw',
      requirements: [
        t('us.eb1a.requirements.1'),
        t('us.eb1a.requirements.2'),
        t('us.eb1a.requirements.3'),
        t('us.eb1a.requirements.4'),
      ],
    },
    {
      title: t('nav.eb1c'),
      description: t('us.eb1c.description'),
      backgroundColor: '#ff9800',
      path: '/us-green-card/eb1c',
      requirements: [
        t('us.eb1c.requirements.1'),
        t('us.eb1c.requirements.2'),
        t('us.eb1c.requirements.3'),
        t('us.eb1c.requirements.4'),
      ],
    },
    {
      title: t('nav.eb5'),
      description: t('us.eb5.description'),
      backgroundColor: '#795548',
      path: '/us-green-card/eb5',
      requirements: [
        t('us.eb5.requirements.1'),
        t('us.eb5.requirements.2'),
        t('us.eb5.requirements.3'),
        t('us.eb5.requirements.4'),
      ],
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5))',
          backgroundColor: '#3f51b5',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          height: '50vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h2" component="h1" gutterBottom>
              {t('us.hero.title')}
            </Typography>
            <Typography variant="h5" gutterBottom>
              {t('us.hero.subtitle')}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Programs Section */}
      <Container sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" align="center" gutterBottom>
          {t('us.programs.title')}
        </Typography>
        <Typography variant="body1" align="center" sx={{ mb: 6 }}>
          {t('us.programs.description')}
        </Typography>
        <Grid container spacing={4}>
          {programs.map((program, index) => (
            <Grid item xs={12} md={4} key={program.path}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                whileHover={{ scale: 1.02 }}
              >
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box
                    sx={{
                      height: 200,
                      backgroundColor: program.backgroundColor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                    }}
                  >
                    <Typography variant="h6">{program.title}</Typography>
                  </Box>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h5" component="h3" gutterBottom>
                      {program.title}
                    </Typography>
                    <Typography variant="body1" paragraph>
                      {program.description}
                    </Typography>
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>{t('us.programs.requirements')}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <ul>
                          {program.requirements.map((req, i) => (
                            <li key={i}>
                              <Typography variant="body2">{req}</Typography>
                            </li>
                          ))}
                        </ul>
                      </AccordionDetails>
                    </Accordion>
                    <Button
                      component={Link}
                      to={program.path}
                      variant="contained"
                      color="primary"
                      fullWidth
                      sx={{ mt: 2 }}
                    >
                      {t('us.programs.learnMore')}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Benefits Section */}
      <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
        <Container>
          <Typography variant="h3" component="h2" align="center" gutterBottom>
            {t('us.benefits.title')}
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            {[
              t('us.benefits.work'),
              t('us.benefits.education'),
              t('us.benefits.healthcare'),
              t('us.benefits.family'),
              t('us.benefits.travel'),
              t('us.benefits.security'),
            ].map((benefit, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card>
                    <CardContent>
                      <Typography variant="h6" component="h3" gutterBottom>
                        {benefit}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default USGreenCard; 